import React, { useState, useEffect, useMemo } from "react"
import PropTypes from "prop-types"
import { Modal, Table, Typography, Tag, Button, Flex } from "antd"
import { CheckOutlined, DeleteOutlined } from "@ant-design/icons"
import { useDispatch, useSelector } from "react-redux"
import { getTripList } from "store/trips"
import { updateAssignedTrips, getGuideDigitalWaivers } from "store/digital-waiver"
import { toaster } from "components/common/Toast"
import ExperienceSelector from "../ExperienceSelector"
import DeleteConfirmation from "../../Deleteconfirmation"
import styles from "../DocumentAdded.module.css"

const { Title, Text } = Typography

// ─── Inner content component ────────────────────────────────────────────────
const AssignExperiencesContent = ({ waiver, onCancel }) => {
  const dispatch = useDispatch()
  const { loading } = useSelector((state) => state.digitalWaiver)
  const [tripsState, setTripsState] = useState([])
  const [selectedExperiences, setSelectedExperiences] = useState([])
  const [deleteTarget, setDeleteTarget] = useState(null)

  useEffect(() => {
    const ids = waiver?.assigned_trips?.map((t) => String(t.id)) || []
    setSelectedExperiences(ids)
  }, [waiver])

  useEffect(() => {
    dispatch(getTripList()).then((response) => {
      if (Array.isArray(response)) {
        setTripsState(response)
      } else if (response && Array.isArray(response.data)) {
        setTripsState(response.data)
      }
    })
  }, [dispatch])

  const experiencesMemo = useMemo(
    () =>
      tripsState.map((trip) => ({
        id: String(trip.id),
        name: trip.title || "Untitled",
        status: trip.status,
        waiver_count: trip.waiver_count ?? 0
      })),
    [tripsState]
  )

  const selectedExperienceData = useMemo(() => {
    if (!selectedExperiences.length) return []
    return selectedExperiences
      .map((id) => {
        const fromTrips = experiencesMemo.find((e) => e.id === String(id))
        if (fromTrips) return fromTrips
        const fromAssigned = waiver?.assigned_trips?.find((t) => String(t.id) === String(id))
        return fromAssigned ? { id: String(fromAssigned.id), name: fromAssigned.title, status: fromAssigned.status } : null
      })
      .filter(Boolean)
  }, [selectedExperiences, experiencesMemo, waiver])

  const handleSave = () => {
    dispatch(updateAssignedTrips(waiver.id, selectedExperiences))
      .then((result) => {
        toaster.success(result?.message || "Experiences updated successfully")
        dispatch(getGuideDigitalWaivers())
        onCancel()
      })
      .catch(() => {
        toaster.error("Failed to update experiences")
      })
  }

  const handleDeleteConfirm = () => {
    const updated = selectedExperiences.filter((id) => id !== String(deleteTarget.id))
    setSelectedExperiences(updated)
    dispatch(updateAssignedTrips(waiver.id, updated))
      .then((result) => {
        toaster.success(result?.message || "Experience removed successfully")
        dispatch(getGuideDigitalWaivers())
        setDeleteTarget(null)
      })
      .catch(() => {
        toaster.error("Failed to remove experience")
        setDeleteTarget(null)
      })
  }

  const columns = [
    {
      title: "Experience Name",
      dataIndex: "name",
      key: "name",
      render: (name) => <Text strong>{name}</Text>
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const isPublished = String(status).toLowerCase() === "published"
        const display = String(status).charAt(0).toUpperCase() + String(status).slice(1)
        return (
          <Tag className={styles.statusTag} color={isPublished ? "green" : "default"} icon={isPublished ? <CheckOutlined /> : null}>
            {display}
          </Tag>
        )
      }
    },
    {
      title: "",
      key: "action",
      width: 50,
      align: "right",
      render: (_, record) => {
        const isDisabled = selectedExperienceData.length === 1
        return (
          <Button
            type="text"
            icon={<DeleteOutlined className={isDisabled ? styles.removeIconDisabled : styles.removeIcon} />}
            onClick={() => setDeleteTarget(record)}
            aria-label="Remove experience"
            disabled={isDisabled}
          />
        )
      }
    }
  ]

  return (
    <div style={{ padding: "16px 0" }}>
      <div style={{ marginBottom: 16 }}>
        <Text strong>Experiences</Text>
        <div style={{ marginTop: 6 }}>
          <ExperienceSelector
            experiences={experiencesMemo}
            value={selectedExperiences}
            onOpen={async () => {
              const response = await dispatch(getTripList())
              if (Array.isArray(response)) {
                setTripsState(response)
              } else if (response && Array.isArray(response.data)) {
                setTripsState(response.data)
              }
            }}
            onChange={(vals) => setSelectedExperiences(vals.map(String))}
          />
        </div>
      </div>

      {selectedExperienceData.length > 0 && (
        <>
          <Text strong style={{ display: "block", marginBottom: 8 }}>Assigned Experiences</Text>
          <Table
            className={styles.table}
            dataSource={selectedExperienceData}
            rowKey="id"
            columns={columns}
            pagination={false}
            scroll={{ y: 300 }}
            locale={{ emptyText: "No experiences assigned." }}
          />
        </>
      )}

      <Flex justify="flex-end" gap={8} style={{ marginTop: 16 }}>
        <Button color="primary" variant="outlined" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button color="primary" variant="solid" loading={loading} disabled={loading} onClick={handleSave}>
          Save
        </Button>
      </Flex>

      <DeleteConfirmation
        visible={!!deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        loading={loading}
        title="Remove Experience"
        content={`Are you sure you want to remove "${deleteTarget?.name}" from this waiver?`}
        confirmText="Remove"
      />
    </div>
  )
}

AssignExperiencesContent.propTypes = {
  waiver: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    assigned_trips: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        title: PropTypes.string,
        status: PropTypes.string
      })
    )
  }),
  onCancel: PropTypes.func.isRequired
}

// ─── Modal wrapper ───────────────────────────────────────────────────────────
const WaiverAssignedExperiencesModal = ({ open, onCancel, waiver }) => {
  return (
    <Modal
      open={open}
      onCancel={onCancel}
      width={700}
      centered
      maskClosable={false}
      footer={null}
      title={
        <Title level={4} style={{ margin: 0 }}>
          Assign Experiences
        </Title>
      }
    >
      {open && <AssignExperiencesContent waiver={waiver} onCancel={onCancel} />}
    </Modal>
  )
}

WaiverAssignedExperiencesModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  waiver: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    assigned_trips: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        title: PropTypes.string,
        status: PropTypes.string
      })
    )
  })
}

export default WaiverAssignedExperiencesModal
